import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect } from "react";

import copy from "../../public/locales/en/analytics.json";
import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchUserActivity,
  resetUserActivity,
  setUserActivityPage,
  setUserActivityPeriod,
} from "../store/slices/userActivitySlice";
import { countryName } from "../utils/analyticsFormatting";

import type { AnalyticsPeriod } from "@lingoshade/domain";

const labels = copy.userActivity;

export default function UserActivityReportPanel({
  userId,
}: {
  userId: string;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const { report, days, page, loading, error } = useAppSelector(
    (state) => state.userActivity,
  );
  useEffect(
    () => (): void => {
      dispatch(resetUserActivity());
    },
    [dispatch, userId],
  );
  useEffect(() => {
    const request = dispatch(fetchUserActivity({ userId, days, page }));
    return (): void => request.abort();
  }, [dispatch, userId, days, page]);
  const current = report?.userId === userId ? report : null;
  return (
    <Paper component="section" sx={{ p: 3, mt: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {labels.title}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          value={days}
          aria-label={copy.period}
          onChange={(_event, value: AnalyticsPeriod | null): void => {
            if (value) dispatch(setUserActivityPeriod(value));
          }}
        >
          {([7, 30, 90] as const).map((period) => (
            <ToggleButton key={period} value={period}>
              {copy[`days${period}`]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          disabled={loading}
          onClick={(): void => {
            void dispatch(fetchUserActivity({ userId, days, page }));
          }}
        >
          {copy.refresh}
        </Button>
      </Box>
      {loading && <CircularProgress size={28} />}
      {error && <Alert severity="error">{labels.error}</Alert>}
      {current && (
        <>
          <Typography component="h3" variant="h6" gutterBottom>
            {labels.acquisitionTitle}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              [
                labels.attributionMethod,
                current.acquisition.method
                  ? (labels.attributionMethods[
                      current.acquisition
                        .method as keyof typeof labels.attributionMethods
                    ] ?? labels.noData)
                  : labels.noData,
              ],
              [
                labels.registeredAt,
                current.acquisition.registeredAt
                  ? new Date(current.acquisition.registeredAt).toLocaleString(
                      "en",
                      { timeZone: "UTC" },
                    )
                  : labels.noData,
              ],
              [labels.country, countryName(current.acquisition.country)],
              [
                copy.campaign.source,
                current.acquisition.attribution?.source ??
                  copy.campaign.unknown,
              ],
              [
                copy.campaign.medium,
                current.acquisition.attribution?.medium ?? labels.noData,
              ],
              [
                copy.campaign.campaign,
                current.acquisition.attribution?.campaign ?? labels.noData,
              ],
              [
                copy.campaign.content,
                current.acquisition.attribution?.content ?? labels.noData,
              ],
              [
                copy.campaign.referrer,
                current.acquisition.attribution?.referrerHost ?? labels.noData,
              ],
              [
                copy.campaign.term,
                current.acquisition.attribution?.term ?? labels.noData,
              ],
            ].map(([label, value]) => (
              <Grid key={label} size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography color="text.secondary">{label}</Typography>
                <Typography sx={{ overflowWrap: "anywhere" }}>
                  {value}
                </Typography>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.countryNote}
          </Typography>
          <Typography component="h3" variant="h6" gutterBottom>
            {labels.summary}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {(
              Object.keys(current.summary) as Array<
                keyof typeof current.summary
              >
            ).map((key) => (
              <Grid key={key} size={{ xs: 6, md: 3 }}>
                <Typography color="text.secondary">{labels[key]}</Typography>
                <Typography variant="h6">{current.summary[key]}</Typography>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.periodNote}
          </Typography>
          <TableContainer>
            <Table size="small" aria-label={labels.title}>
              <TableHead>
                <TableRow>
                  {[
                    copy.date,
                    labels.action,
                    labels.content,
                    labels.language,
                    labels.duration,
                    labels.result,
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {current.events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {new Date(event.at).toLocaleString("en", {
                        timeZone: "UTC",
                      })}
                    </TableCell>
                    <TableCell>{labels.kinds[event.kind]}</TableCell>
                    <TableCell>{event.label ?? labels.noData}</TableCell>
                    <TableCell>{event.language}</TableCell>
                    <TableCell>{event.seconds || "—"}</TableCell>
                    <TableCell>
                      {event.correct === null
                        ? "—"
                        : event.correct
                          ? labels.correct
                          : labels.incorrect}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {current.events.length === 0 && (
            <Typography sx={{ mt: 2 }}>{labels.empty}</Typography>
          )}
          {current.totalEvents > current.pageSize && (
            <Pagination
              sx={{ mt: 2 }}
              aria-label={labels.pagination}
              count={Math.ceil(current.totalEvents / current.pageSize)}
              page={page}
              onChange={(_event, value): void => {
                dispatch(setUserActivityPage(value));
              }}
            />
          )}
        </>
      )}
    </Paper>
  );
}
